'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { InViewPosition } from '@/libraries/hooks/use-is-in-view';

type NavigationBehavior = 'concrete' | 'dynamic';

export interface PageDetailsContextState {
  activeModal: Content.Components['ModalType'];
  bookingType?: Content.Components['BookingType'];
  brand: Content.Properties.Brand;
  configurationFlowAnchorLinkId?: string;
  ctaRefPosition: InViewPosition;
  destinationId?: number;
  handleUpdateIsScrolled: (value: boolean) => void;
  hasInPageNavigation: boolean;
  hasTransparentNavigation?: boolean;
  hotelDetails?: Content.PageType.HotelPage['hotelDetails'];
  isInWebview?: boolean;
  isScrolled: boolean;
  isVisibleInPageConfigurationFlow: boolean;
  isVisibleInPageHeader: boolean;
  offerDetails?: Content.PageType.HotelPage['offerDetails'];
  pageType?: string;
  searchDetails?: Content.Components['HotelPageOfferDetailsDto'];
  searchWidgetRefPosition: InViewPosition;
  searchWidgetState?: Content.PageType.HotelPage['searchWidgetState'];
  setActiveModal: (value: Content.Components['ModalType']) => void;
  setConfigurationFlowAnchorLinkId: (value?: string) => void;
  setCtaRefPosition: (value: InViewPosition) => void;
  setDestinationId: (value?: number) => void;
  setHasInPageNavigation: (value: boolean) => void;
  setIsInWebview: (value: boolean) => void;
  setIsScrolled: (value: boolean) => void;
  setIsVisibleInPageConfigurationFlow: (value: boolean) => void;
  setIsVisibleInPageHeader: (value: boolean) => void;
  setNavigationBehavior: (value: NavigationBehavior) => void;
  setSearchDetails: (
    value: Content.Components['HotelPageOfferDetailsDto'],
  ) => void;
  setSearchWidgetRefPosition: (value: InViewPosition) => void;
  setStickyTopContentHeight: (value: number) => void;
  setTrackingData: (value: Content.Properties.TrackingData) => void;
  shouldUpdateIsScrolled: boolean;
  stickyTopContentHeight: number;
  trackingData?: Content.Properties.TrackingData;
}

const PageDetailsContext = createContext<PageDetailsContextState | undefined>(
  undefined,
);

export const PageDetailsProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData: {
    bookingType?: Content.Components['BookingType'];
    brand: Content.Properties.Brand;
    ctaRefPosition?: InViewPosition;
    destinationId?: number;
    hasInPageNavigation?: boolean;
    hasTransparentNavigation?: boolean;
    hotelDetails?: Content.PageType.HotelPage['hotelDetails'];
    isInWebview?: boolean;
    isVisibleInPageConfigurationFlow?: boolean;
    isVisibleInPageHeader?: boolean;
    offerDetails?: Content.PageType.HotelPage['offerDetails'];
    pageType?: string;
    searchDetails?: Content.Components['HotelPageOfferDetailsDto'];
    searchWidgetRefPosition?: InViewPosition;
    searchWidgetState?: Content.PageType.HotelPage['searchWidgetState'];
    shouldUpdateIsScrolled?: boolean;
    stickyTopContentHeight?: number;
    trackingData?: Content.Properties.TrackingData;
  };
}) => {
  const [activeModal, setActiveModal] =
    useState<Content.Components['ModalType']>('none');
  const [configurationFlowAnchorLinkId, setConfigurationFlowAnchorLinkId] =
    useState<string>();
  const [ctaRefPosition, setCtaRefPosition] = useState<InViewPosition>(
    initialData?.ctaRefPosition ?? null,
  );
  const [destinationId, setDestinationId] = useState(initialData.destinationId);
  const [hasInPageNavigation, setHasInPageNavigation] = useState(
    initialData?.hasInPageNavigation ?? false,
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [isInWebview, setIsInWebview] = useState(
    initialData?.isInWebview ?? false,
  );
  const [
    isVisibleInPageConfigurationFlow,
    setIsVisibleInPageConfigurationFlow,
  ] = useState(initialData?.isVisibleInPageConfigurationFlow ?? false);

  const [isVisibleInPageHeader, setIsVisibleInPageHeader] = useState(
    initialData?.isVisibleInPageHeader ?? false,
  );
  const [navigationBehavior, setNavigationBehavior] =
    useState<NavigationBehavior>('dynamic');
  const [searchDetails, setSearchDetails] = useState<
    Content.Components['HotelPageOfferDetailsDto'] | undefined
  >(initialData?.searchDetails);
  const [searchWidgetRefPosition, setSearchWidgetRefPosition] =
    useState<InViewPosition>(initialData?.searchWidgetRefPosition ?? null);
  const [shouldUpdateIsScrolled, setShouldUpdateIsScrolled] = useState(
    initialData?.shouldUpdateIsScrolled ?? true,
  );
  const [stickyTopContentHeight, setStickyTopContentHeight] = useState(
    initialData?.stickyTopContentHeight ?? 88,
  );
  const [trackingData, setTrackingData] = useState(initialData.trackingData);

  const handleUpdateIsScrolled = (value: boolean) => {
    if (navigationBehavior === 'concrete') {
      setShouldUpdateIsScrolled(false);

      return;
    }

    setShouldUpdateIsScrolled(value);
  };

  useEffect(() => {
    if (navigationBehavior === 'concrete') {
      setShouldUpdateIsScrolled(false);
    }

    if (navigationBehavior === 'dynamic') {
      setShouldUpdateIsScrolled(true);
    }
  }, [navigationBehavior]);

  return (
    <PageDetailsContext.Provider
      value={{
        activeModal,
        bookingType: initialData.bookingType,
        brand: initialData.brand,
        configurationFlowAnchorLinkId,
        ctaRefPosition,
        destinationId,
        handleUpdateIsScrolled,
        hasInPageNavigation,
        hasTransparentNavigation: initialData?.hasTransparentNavigation,
        hotelDetails: initialData.hotelDetails,
        isInWebview,
        isScrolled,
        isVisibleInPageConfigurationFlow,
        isVisibleInPageHeader,
        offerDetails: initialData.offerDetails,
        pageType: initialData.pageType,
        searchDetails,
        searchWidgetRefPosition,
        searchWidgetState: initialData.searchWidgetState,
        setActiveModal,
        setConfigurationFlowAnchorLinkId,
        setCtaRefPosition,
        setDestinationId,
        setHasInPageNavigation,
        setIsInWebview,
        setIsScrolled,
        setIsVisibleInPageConfigurationFlow,
        setIsVisibleInPageHeader,
        setNavigationBehavior,
        setSearchDetails,
        setSearchWidgetRefPosition,
        setStickyTopContentHeight,
        setTrackingData,
        shouldUpdateIsScrolled,
        stickyTopContentHeight,
        trackingData,
      }}
    >
      {children}
    </PageDetailsContext.Provider>
  );
};

export const usePageDetailsContext = () => {
  const context = useContext(PageDetailsContext);

  if (!context) {
    throw new Error(
      'usePageDetailsContext must be used within a PageDetailsProvider',
    );
  }

  return context;
};
